import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';

import * as api from '../../../api';

import { ReactComponent as CalendarSvg } from '../../../assets/calendar.svg';

import ProviderInfo from './ProviderView/ProviderInfo';
import PracticeInfo from './PracticeView/PracticeInfo';

import { type ProviderAndPractice, type PracticeOnly } from './common';
import Filters from '../Filters';
import { SchedulingContext } from '../../../scenes/Form/ScheduleAppointmentPage/SchedulingContext';

import styles from '../index.module.css';
import { AlertCircle } from '@vivaahealth/design-system/dist/components/Icon';
import { Typography } from '@vivaahealth/design-system';
import AppointmentView from './AppointmentView';

const AlternateProviderView: React.FC<{
  mainProviderHasSlots: boolean;
  mainProviderId: string | null;
  mainPracticeId: string;
  providerAndPractice: ProviderAndPractice[] | PracticeOnly[];
}> = ({
  mainProviderHasSlots,
  mainProviderId,
  mainPracticeId,
  providerAndPractice,
}) => {
  const {
    patientPcpPreferred,
    patientOrder,
    availableEhrTimeSlots,
    filterProps,
    fieldConfig,
    onAppointmentSelected,
    providers,
    practices,
  } = useContext(SchedulingContext);
  const { scheduleAppointmentDisplayBy } = fieldConfig;
  const { t } = useTranslation();

  const getMainProviderAndPractice = (): ProviderAndPractice | undefined => {
    const mainProvider = mainProviderId ? providers[mainProviderId] : undefined;
    const mainPractice = practices[mainPracticeId];
    if (
      mainProvider &&
      scheduleAppointmentDisplayBy ===
        api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
          .Provider
    ) {
      return {
        ..._.pick(
          mainProvider,
          'firstName',
          'lastName',
          'prefix',
          'suffix',
          'previsitImageUrl',
          'phoneNumber',
        ),
        ...mainPractice,
        practiceId: mainPracticeId,
        providerUserId: mainProviderId!,
        specialties: mainProvider.specialties ?? null,
      };
    }
    return;
  };

  return (
    <div className={styles.AlternateProviderContainer}>
      {getMainProviderAndPractice() !== undefined && !mainProviderHasSlots && (
        <div className={styles.AlternateProviderListItem}>
          <div className={styles.AlternateProviderItem}>
            <ProviderInfo
              config={getMainProviderAndPractice()!}
              hasAlternateProvider={
                !!patientOrder?.alternateReferredToProvider ||
                providerAndPractice.length > 1
              }
            />
            <div className={styles.NoAppointmentsMessageContainer}>
              <CalendarSvg
                viewBox="0 0 28 24"
                className={styles.ProviderCalendarIcon}
              />
              <Typography variant="paragraph-medium" bold>
                {t('No appointments available')}
              </Typography>
            </div>
          </div>
        </div>
      )}
      {providerAndPractice.map((config, i) => {
        const distance = availableEhrTimeSlots.find(
          (slot) => slot.practiceId === config.practiceId,
        )?.distance;
        return (
          'providerUserId' in config && (
            <div key={`${config.practiceId}${config.providerUserId}`}>
              <div className={styles.AlternateProviderListItem}>
                {config.providerUserId !== mainProviderId && i === 1 && (
                  <>
                    <AlternatesInfoPanel
                      mainProviderHasSlots={mainProviderHasSlots}
                      hasPatientOrderAlternateProvider={
                        !!patientOrder?.alternateReferredToProvider
                      }
                    />
                    {!!filterProps && (
                      <Filters
                        onFiltersChange={filterProps.onFiltersChange}
                        filters={filterProps.filters}
                        displayBy={scheduleAppointmentDisplayBy ?? undefined}
                        daysAllowedFrom={filterProps.daysAllowedFrom}
                        daysAllowedTo={filterProps.daysAllowedTo}
                        sortProviderAlternatesOnly={true}
                        patientPcpPreferred={patientPcpPreferred}
                      />
                    )}
                  </>
                )}
                <div className={styles.AlternateProviderItem}>
                  {scheduleAppointmentDisplayBy ===
                  api.PrevisitFormTemplateFieldConfig
                    .ScheduleAppointmentDisplayBy.Provider ? (
                    <ProviderInfo
                      config={config}
                      distance={distance}
                      hasAlternateProvider={
                        !!patientOrder?.alternateReferredToProvider ||
                        providerAndPractice.length > 1
                      }
                    />
                  ) : (
                    <PracticeInfo config={config} distance={distance} />
                  )}
                  <div className={styles.AlternateProviderAppointmentContainer}>
                    <AppointmentView
                      timeSlots={availableEhrTimeSlots}
                      config={config}
                      onAppointmentSelected={onAppointmentSelected}
                      fieldConfig={fieldConfig}
                      patientOrder={patientOrder}
                    />
                  </div>
                </div>
                {i === providerAndPractice.length - 1 && (
                  <div
                    className={styles.AlternateProviderListItemDivider}
                  ></div>
                )}
              </div>
            </div>
          )
        );
      })}
    </div>
  );
};

const AlternatesInfoPanel: React.FC<{
  mainProviderHasSlots: boolean;
  hasPatientOrderAlternateProvider: boolean;
}> = ({ mainProviderHasSlots, hasPatientOrderAlternateProvider }) => {
  const { t } = useTranslation();
  const message = !hasPatientOrderAlternateProvider
    ? 'These other providers are also available.'
    : mainProviderHasSlots
      ? "Your provider has designated the below alternate if the above availability doesn't fit your schedule."
      : "Since your provider has no upcoming availability, they've designated the below provider as an alternate so that you can schedule sooner.";
  return (
    <div
      className={styles.AlternatesInfoPanel}
      aria-label="Alternate provider info panel"
    >
      <AlertCircle className={styles.AlternatesInfoPanelIcon} />
      <Typography variant="title-medium" className="my-auto">
        {t(message)}
      </Typography>
    </div>
  );
};

export default AlternateProviderView;
